import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useComponentPromotionState, type PromotionStateRow } from '@/hooks/useComponentPromotionState';

interface Props {
  shopId: string | null;
}

type MaturityBucket = 'learning' | 'stable' | 'advancing' | 'regressing' | 'automation_candidate';

const BUCKET_META: Record<MaturityBucket, { label: string; className: string }> = {
  learning: { label: 'Learning', className: 'bg-muted text-foreground border-muted-foreground/30' },
  stable: { label: 'Stable', className: 'bg-secondary text-secondary-foreground border-secondary' },
  advancing: { label: 'Advancing', className: 'bg-primary/15 text-primary border-primary/30' },
  regressing: { label: 'Regressing', className: 'bg-destructive/15 text-destructive border-destructive/30' },
  automation_candidate: { label: 'Automation candidate', className: 'bg-accent/15 text-accent-foreground border-accent/30' },
};

function classify(row: PromotionStateRow): MaturityBucket {
  if (row.recommended_action === 'demote' || row.recommended_action === 'hard_demote') return 'regressing';
  if (row.current_stage === 'automation_candidate') return 'automation_candidate';
  if (row.recommended_action === 'promote') return 'advancing';
  if (row.current_stage === 'job_suggestion' || row.current_stage === 'prefill') return 'stable';
  return 'learning';
}

export function ComponentMaturityMap({ shopId }: Props) {
  const { data: rows = [], isLoading } = useComponentPromotionState(shopId);

  if (isLoading) {
    return (
      <Card>
        <CardHeader><CardTitle className="text-base">Component Maturity Map</CardTitle></CardHeader>
        <CardContent><div className="text-sm text-muted-foreground">Loading…</div></CardContent>
      </Card>
    );
  }

  const grouped = rows.reduce<Record<MaturityBucket, PromotionStateRow[]>>((acc, r) => {
    const b = classify(r);
    (acc[b] ||= []).push(r);
    return acc;
  }, { learning: [], stable: [], advancing: [], regressing: [], automation_candidate: [] });

  const orderedBuckets: MaturityBucket[] = ['regressing', 'advancing', 'automation_candidate', 'stable', 'learning'];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Component Maturity Map</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {rows.length === 0 ? (
          <div className="text-sm text-muted-foreground">
            No components evaluated yet — promotion engine runs every 15 minutes once findings accumulate.
          </div>
        ) : (
          orderedBuckets.map((b) => {
            const items = grouped[b];
            if (items.length === 0) return null;
            const meta = BUCKET_META[b];
            return (
              <div key={b} className="space-y-2">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className={meta.className}>{meta.label}</Badge>
                  <span className="text-xs text-muted-foreground">{items.length}</span>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                  {items.map((r) => (
                    <div key={r.id} className="border rounded p-2 space-y-1">
                      <div className="text-xs font-medium text-foreground truncate" title={r.component}>
                        {r.component}
                      </div>
                      <div className="text-[10px] text-muted-foreground truncate">{r.condition_type}</div>
                      <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                        <span>{r.current_stage}</span>
                        <span>P {r.promotion_score.toFixed(2)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
